import type {
  DiagnosticsReport,
  SyncFailureDetail,
  SyncRun,
} from "../api/types";

/**
 * El informe de diagnóstico, en texto y listo para pegar.
 *
 * PARA QUIÉN SE ESCRIBE
 *   Para quien vaya a leerlo fuera de la aplicación: otra persona, o una conversación en la que se
 *   pide ayuda. Por eso es Markdown sencillo y por eso las horas van en **ISO y en UTC**: pegado en
 *   cualquier sitio, `2026-08-28T07:15:00.000Z` significa lo mismo, mientras que «28/08, 9:15» solo
 *   se entiende sabiendo desde qué reloj se escribió. La zona del servidor se dice una vez arriba.
 *
 * LO PRIMERO, LO QUE FALLÓ
 *   Las sincronizaciones van de la más reciente a la más antigua y cada error trae **todas** las
 *   rutas que dejaron de encajar. Es lo único que no se puede reconstruir después: el resto
 *   (versión, recuentos, horarios) se puede volver a mirar, pero el detalle de un fallo concreto se
 *   pierde con la siguiente sincronización que lo tape.
 *
 * NO SE INVENTA NADA
 *   Lo que no viene en el informe no se rellena: un hueco es un hueco y se escribe como tal.
 */

/** Separador de las listas de una línea, el mismo que usa la interfaz. */
const PUNTO = " · ";

export function formatDiagnostics(report: DiagnosticsReport): string {
  return [
    "# Informe de Biwenger Manager",
    "",
    ...cabecera(report),
    "",
    "## Qué hay en la base",
    "",
    recuentos(report.counts),
    "",
    `## Últimas sincronizaciones (${String(report.runs.length)})`,
    "",
    ...(report.runs.length === 0
      ? ["No hay ninguna sincronización registrada."]
      : report.runs.flatMap((run) => describirRun(run))),
    "",
    "_Las horas van en UTC (ISO-8601). Este informe no incluye el token de Biwenger ni los" +
      " identificadores de liga y usuario._",
    "",
  ].join("\n");
}

/* --------------------------------------------------------------------- las secciones ---- */

function cabecera(report: DiagnosticsReport): string[] {
  const { connection, sync } = report;
  return [
    `- Generado: ${report.generatedAt}`,
    `- Versión ${report.version}${PUNTO}Node ${report.nodeVersion}${PUNTO}entorno ${report.nodeEnv}${PUNTO}zona ${report.timezone}`,
    `- Conexión: modo ${connection.mode}${PUNTO}${
      connection.configured ? "configurada" : "SIN CONFIGURAR"
    }${PUNTO}competición ${connection.competition}${PUNTO}baremo ${connection.scoreId}`,
    `- Última sincronización correcta: ${sync.lastOkAt ?? "ninguna"}`,
    `- Aviso de token caducado: ${sync.authError ? "SÍ" : "no"}`,
    `- Horarios: ${horarios(sync.cron)}${PUNTO}cuota diaria de fichas ${String(
      sync.playersDailyQuota,
    )}`,
  ];
}

function horarios(cron: Record<string, string>): string {
  const entradas = Object.entries(cron);
  if (entradas.length === 0) return "sin horarios";
  return entradas
    .map(([nombre, expresion]) => `${nombre} «${expresion}»`)
    .join(PUNTO);
}

function recuentos(counts: Record<string, number>): string {
  const entradas = Object.entries(counts);
  if (entradas.length === 0) return "No se ha podido contar nada.";
  return entradas
    .map(
      ([tabla, total]) =>
        // `-1` es lo que devuelve el servidor cuando una tabla no se deja contar: decirlo es más
        // útil que enseñar un cero que se leería como «está vacía».
        `${tabla} ${total < 0 ? "(no se ha podido contar)" : String(total)}`,
    )
    .join(PUNTO);
}

/* ------------------------------------------------------------------ una sincronización -- */

function describirRun(run: SyncRun): string[] {
  const estado = run.status === "error" ? "ERROR" : run.status;
  const lineas = [
    `### #${String(run.id)}${PUNTO}${run.kind}${PUNTO}${estado}`,
    "",
    `- Empezó: ${run.startedAt}`,
    `- Terminó: ${run.finishedAt ?? "sigue en marcha"}${duracion(run)}`,
  ];

  if (run.message !== null) lineas.push(`- Mensaje: ${run.message}`);
  lineas.push(`- Recuentos: ${estadisticas(run.stats)}`);

  const detalle = describirDetalle(run.detail);
  if (detalle.length > 0) lineas.push("", ...detalle);

  lineas.push("");
  return lineas;
}

/** Cuánto duró, entre paréntesis. Se calla si no hay con qué calcularlo. */
function duracion(run: SyncRun): string {
  if (run.finishedAt === null) return "";
  const ms = Date.parse(run.finishedAt) - Date.parse(run.startedAt);
  if (!Number.isFinite(ms) || ms < 0) return "";
  if (ms < 1000) return ` (${String(ms)} ms)`;
  const segundos = Math.round(ms / 1000);
  if (segundos < 90) return ` (${String(segundos)} s)`;
  return ` (${String(Math.round(segundos / 60))} min)`;
}

function estadisticas(stats: Record<string, number>): string {
  const entradas = Object.entries(stats);
  if (entradas.length === 0) return "ninguno";
  return entradas
    .map(([clave, valor]) => `${clave} ${String(valor)}`)
    .join(PUNTO);
}

/**
 * El fallo con estructura. Los desajustes de esquema van uno por línea porque es lo que se lee
 * buscando el patrón: tres campos que desaparecen juntos cuentan algo que uno solo no cuenta.
 */
function describirDetalle(detail: SyncFailureDetail | null): string[] {
  if (detail === null) return [];

  switch (detail.kind) {
    case "parse":
      return [
        `Desajustes con el esquema de «${detail.resource}» (${String(
          detail.issues.length,
        )}):`,
        ...detail.issues.map(
          (issue) => `- \`${issue.path}\` — ${issue.message}`,
        ),
      ];
    case "auth":
      return ["Biwenger rechazó las credenciales (401): el token ha caducado."];
    case "offline":
      return ["El candado de salida a la red estaba echado: no se pidió nada."];
    case "not_configured":
      return ["La conexión con Biwenger no estaba configurada."];
    case "http":
      return [
        `Biwenger respondió ${String(detail.status)} tras ${String(
          detail.attempts,
        )} intento(s).`,
      ];
    case "network":
      return [
        `No se pudo contactar con Biwenger tras ${String(
          detail.attempts,
        )} intento(s).`,
      ];
  }
}
